<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <!-- Senza cron le pianificazioni sono configurabili ma inerti: dirlo,
         invece di lasciar credere che siano attive. -->
    <div v-if="!schedulerHealthy" class="scheduler-warning">
      <div class="scheduler-warning-text">
        <span class="scheduler-warning-icon">⚠️</span>
        <oxd-text tag="span">{{ $t('time.scheduler_not_running') }}</oxd-text>
      </div>
      <pre class="scheduler-cron-line">{{ cronLine }}</pre>
    </div>

    <div
      v-for="schedule in schedules"
      :key="schedule.id"
      class="orangehrm-card-container"
    >
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ scheduleLabel(schedule.type) }}
      </oxd-text>

      <oxd-divider />

      <oxd-form
        :loading="busyId === schedule.id"
        @submit-valid="onSave(schedule)"
      >
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="schedule.enabled"
                type="checkbox"
                :label="$t('time.notification_enabled')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="schedule.dayOfMonth"
                :label="$t('time.notification_day_of_month')"
                :rules="rules.dayOfMonth"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="schedule.hour"
                :label="$t('time.notification_time') + ' (h)'"
                :rules="rules.hour"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="schedule.minute"
                :label="$t('time.notification_time') + ' (m)'"
                :rules="rules.minute"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-text tag="p" class="schedule-timezone">
            {{ $t('time.notification_timezone') }}: {{ timezone }}
          </oxd-text>
        </oxd-form-row>

        <oxd-form-row v-if="schedule.type === 'MONTHLY_EXPORT'">
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="schedule.recipients"
                type="textarea"
                :label="$t('time.notification_recipients')"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <oxd-button
            display-type="ghost"
            :label="$t('time.notification_run_now')"
            :disabled="busyId !== null"
            @click="onRunNow(schedule)"
          />
          <oxd-button
            type="submit"
            display-type="secondary"
            :label="$t('general.save')"
          />
        </oxd-form-actions>
      </oxd-form>
    </div>

    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('time.notification_recent_runs') }}
      </oxd-text>

      <oxd-divider />

      <table v-if="logs.length > 0" class="run-log-table">
        <thead>
          <tr>
            <th>{{ $t('general.date') }}</th>
            <th>{{ $t('general.type') }}</th>
            <th>{{ $t('general.status') }}</th>
            <th class="run-log-numeric">
              {{ $t('time.notification_recipient_count') }}
            </th>
            <th>{{ $t('general.description') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(log, index) in logs" :key="index">
            <td>{{ log.runAt }}</td>
            <td>
              {{ scheduleLabel(log.type) }}
              <span v-if="log.manual" class="run-log-manual">
                ({{ $t('time.notification_manual_run') }})
              </span>
            </td>
            <td :class="'run-log-' + log.status.toLowerCase()">
              {{ log.status }}
            </td>
            <td class="run-log-numeric">{{ log.recipientCount }}</td>
            <td>{{ log.message }}</td>
          </tr>
        </tbody>
      </table>

      <oxd-text v-else>{{ $t('time.notification_no_runs') }}</oxd-text>
    </div>
  </div>
</template>

<script>
import {ref} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import useToast from '@/core/util/composable/useToast';

const TYPE_LABELS = {
  MONTHLY_EXPORT: 'time.notification_monthly_export',
  TIMESHEET_ANOMALY: 'time.notification_anomaly',
  TIMESHEET_REMINDER: 'time.notification_reminder',
};

export default {
  name: 'NotificationSchedules',

  setup() {
    const toast = useToast();
    const schedules = ref([]);
    const logs = ref([]);
    const timezone = ref('');
    const schedulerHealthy = ref(true);
    const busyId = ref(null);

    const scheduleHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/time/notification-schedules',
    );
    const runHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/time/notification-runs',
    );

    return {
      toast,
      schedules,
      logs,
      timezone,
      schedulerHealthy,
      busyId,
      scheduleHttp,
      runHttp,
    };
  },

  computed: {
    cronLine() {
      // La riga realmente installata in produzione: `-u www-data` non e' un
      // dettaglio, senza il cron scrive cache e log come root e Apache poi
      // non riesce piu' a riscriverli.
      return (
        '* * * * * /usr/bin/docker exec -w /var/www/html -u www-data ' +
        'orangehrm-web php bin/console orangehrm:run-schedule >/dev/null 2>&1'
      );
    },

    rules() {
      return {
        dayOfMonth: [this.rangeRule(1, 31)],
        hour: [this.rangeRule(0, 23)],
        minute: [this.rangeRule(0, 59)],
      };
    },
  },

  mounted() {
    this.fetch();
  },

  methods: {
    rangeRule(min, max) {
      return (value) => {
        const parsed = Number(value);
        return (
          (Number.isInteger(parsed) && parsed >= min && parsed <= max) ||
          `${min} - ${max}`
        );
      };
    },

    scheduleLabel(type) {
      const key = TYPE_LABELS[type];
      return key ? this.$t(key) : type;
    },

    async fetch() {
      try {
        const response = await this.scheduleHttp.getAll();
        const data = response.data?.data;
        if (!data) return;
        this.schedules = data.schedules ?? [];
        this.logs = data.logs ?? [];
        this.timezone = data.timezone ?? '';
        this.schedulerHealthy = data.schedulerHealthy ?? false;
      } catch (error) {
        this.schedulerHealthy = false;
      }
    },

    async onSave(schedule) {
      this.busyId = schedule.id;
      try {
        await this.scheduleHttp.update(schedule.id, {
          enabled: schedule.enabled,
          dayOfMonth: Number(schedule.dayOfMonth),
          hour: Number(schedule.hour),
          minute: Number(schedule.minute),
          recipients: schedule.recipients,
        });
        this.toast.updateSuccess();
        this.fetch();
      } finally {
        this.busyId = null;
      }
    },

    async onRunNow(schedule) {
      this.busyId = schedule.id;
      try {
        const response = await this.runHttp.create({type: schedule.type});
        const result = response.data?.data ?? {};

        if (result.status === 'FAILURE') {
          this.toast.error(result.message ?? '', '');
        } else {
          this.toast.success(this.$t('time.notification_run_queued'), '');
        }

        this.fetch();
      } finally {
        this.busyId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.scheduler-warning {
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  border: 1px solid #d9534f;
  background-color: rgba(217, 83, 79, 0.1);
}

.scheduler-warning-text {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.scheduler-warning-icon {
  font-size: 1rem;
}

.scheduler-cron-line {
  margin: 0.6rem 0 0;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  background-color: #2f2f2f;
  color: #f5f5f5;
  font-size: 0.75rem;
  overflow-x: auto;
}

.schedule-timezone {
  font-size: 0.75rem;
  font-style: italic;
  opacity: 0.75;
}

.run-log-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;

  th,
  td {
    text-align: left;
    padding: 0.35rem 0.5rem;
    border-bottom: 1px solid #e0e0e0;
  }

  th {
    font-weight: 600;
  }
}

.run-log-numeric {
  text-align: right;
}

.run-log-manual {
  font-style: italic;
  opacity: 0.7;
}

.run-log-success {
  color: #17a54a;
}

.run-log-failure {
  color: #d9534f;
}

.run-log-partial {
  color: #b06d00;
}
</style>
